import React, { useState } from 'react';
import { Shield, Bell, Globe, Key, Save, RotateCcw, ToggleLeft, ToggleRight, Database } from 'lucide-react';

interface ComplianceRule {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
}

const initialRules: ComplianceRule[] = [
  { id: 'r-1', name: 'PII Detection', description: 'Scan for personally identifiable information in uploaded documents', enabled: true },
  { id: 'r-2', name: 'Signature Verification', description: 'Validate assessor signatures against registered QSA records', enabled: true },
  { id: 'r-3', name: 'Evidence Tracing', description: 'Cross-reference all cited evidence with submitted documentation', enabled: true },
  { id: 'r-4', name: 'Template Compliance', description: 'Verify document structure matches the uploaded compliance template format', enabled: false },
];

export const Settings: React.FC = () => {
  const [defaultTemplate, setDefaultTemplate] = useState('auto-detect');
  const [qualityThreshold, setQualityThreshold] = useState('70');
  const [notifEmail, setNotifEmail] = useState(true);
  const [notifSlack, setNotifSlack] = useState(true);
  const [notifCritical, setNotifCritical] = useState(true);
  const [apiEndpoint, setApiEndpoint] = useState('https://api.nimq.io/v2/trace');
  const [rules, setRules] = useState<ComplianceRule[]>(initialRules);
  const [saved, setSaved] = useState(false);

  const toggleRule = (id: string) => {
    setRules(prev => prev.map(r => r.id === id ? { ...r, enabled: !r.enabled } : r));
  };

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="max-w-4xl space-y-6">
      {/* Analysis Configuration */}
      <div className="glass-card p-6 animate-slide-up">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 rounded-xl bg-brand-50 flex items-center justify-center">
            <Database size={18} className="text-brand-400" />
          </div>
          <div>
            <h3 className="section-title">Analysis Configuration</h3>
            <p className="section-subtitle">Default settings for document analysis</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-[11px] font-semibold text-surface-500 uppercase tracking-wider">
              Default Template
            </label>
            <select
              value={defaultTemplate}
              onChange={(e) => setDefaultTemplate(e.target.value)}
              className="select-field mt-1"
            >
              <option value="auto-detect">Auto-detect from uploaded template</option>
              <option value="custom">Custom uploaded template</option>
            </select>
            <p className="text-[11px] text-surface-400 mt-1">Applied when no template is selected during upload</p>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-surface-500 uppercase tracking-wider">
              Quality Threshold (%)
            </label>
            <input
              type="number"
              value={qualityThreshold}
              onChange={(e) => setQualityThreshold(e.target.value)}
              min="0"
              max="100"
              className="input-field mt-1"
            />
            <p className="text-[11px] text-surface-400 mt-1">Minimum score for "Satisfactory" rating</p>
          </div>
        </div>
      </div>

      {/* Notification Preferences */}
      <div className="glass-card p-6 animate-slide-up">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
            <Bell size={18} className="text-blue-600" />
          </div>
          <div>
            <h3 className="section-title">Notification Preferences</h3>
            <p className="section-subtitle">Configure how and when you receive alerts</p>
          </div>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-surface-50 rounded-xl">
            <div>
              <p className="text-sm font-medium text-surface-700">Email Notifications</p>
              <p className="text-xs text-surface-500">Receive alerts at sarah@nimq.io</p>
            </div>
            <button onClick={() => setNotifEmail(!notifEmail)}>
              {notifEmail ? <ToggleRight size={28} className="text-brand-400" /> : <ToggleLeft size={28} className="text-surface-300" />}
            </button>
          </div>
          <div className="flex items-center justify-between p-3 bg-surface-50 rounded-xl">
            <div>
              <p className="text-sm font-medium text-surface-700">Slack Notifications</p>
              <p className="text-xs text-surface-500">Post to #trace-alerts channel</p>
            </div>
            <button onClick={() => setNotifSlack(!notifSlack)}>
              {notifSlack ? <ToggleRight size={28} className="text-brand-400" /> : <ToggleLeft size={28} className="text-surface-300" />}
            </button>
          </div>
          <div className="flex items-center justify-between p-3 bg-surface-50 rounded-xl">
            <div>
              <p className="text-sm font-medium text-surface-700">Critical Alerts Only</p>
              <p className="text-xs text-surface-500">Only notify on critical severity findings</p>
            </div>
            <button onClick={() => setNotifCritical(!notifCritical)}>
              {notifCritical ? <ToggleRight size={28} className="text-brand-400" /> : <ToggleLeft size={28} className="text-surface-300" />}
            </button>
          </div>
        </div>
      </div>

      {/* API Configuration */}
      <div className="glass-card p-6 animate-slide-up">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 rounded-xl bg-violet-50 flex items-center justify-center">
            <Globe size={18} className="text-violet-600" />
          </div>
          <div>
            <h3 className="section-title">API Configuration</h3>
            <p className="section-subtitle">Manage API endpoints and authentication</p>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-[11px] font-semibold text-surface-500 uppercase tracking-wider">API Endpoint</label>
            <input
              type="text"
              value={apiEndpoint}
              onChange={(e) => setApiEndpoint(e.target.value)}
              className="input-field mt-1 font-mono"
            />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-surface-500 uppercase tracking-wider">API Key</label>
            <div className="mt-1 flex items-center gap-2">
              <input
                type="password"
                value="sk-trace-xxxxxxxxxxxxxxxxxxxx"
                readOnly
                className="flex-1 text-sm font-mono border border-surface-200 rounded-xl px-4 py-2.5 bg-surface-50 text-surface-400"
              />
              <button className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium text-surface-600 bg-surface-100 border border-surface-200 rounded-xl hover:bg-surface-200 transition-colors">
                <Key size={14} /> Regenerate
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Compliance Rules */}
      <div className="glass-card animate-slide-up">
        <div className="px-6 pt-6 pb-4 border-b border-surface-100 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center">
            <Shield size={18} className="text-emerald-600" />
          </div>
          <div>
            <h3 className="section-title">Compliance Rules</h3>
            <p className="section-subtitle">Enable or disable specific analysis checks</p>
          </div>
        </div>
        <div className="divide-y divide-surface-50">
          {rules.map(rule => (
            <div key={rule.id} className="px-6 py-4 flex items-center justify-between">
              <div className="flex-1">
                <span className="text-sm font-medium text-surface-800">{rule.name}</span>
                <p className="text-xs text-surface-500 mt-0.5">{rule.description}</p>
              </div>
              <button onClick={() => toggleRule(rule.id)} className="ml-4 flex-shrink-0">
                {rule.enabled ? (
                  <ToggleRight size={28} className="text-brand-400" />
                ) : (
                  <ToggleLeft size={28} className="text-surface-300" />
                )}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Save Bar */}
      <div className="flex items-center justify-end gap-3 pb-4">
        <button className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-surface-600 bg-surface-100 rounded-xl hover:bg-surface-200 transition-colors">
          <RotateCcw size={14} /> Reset to Defaults
        </button>
        <button
          onClick={handleSave}
          className={`flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-white rounded-xl transition-all shadow-sm ${saved ? 'bg-emerald-500 shadow-emerald-500/20' : 'bg-brand-400 hover:bg-brand-500 shadow-brand-400/20'
            }`}
        >
          {saved ? <><Save size={14} /> Saved!</> : <><Save size={14} /> Save Changes</>}
        </button>
      </div>
    </div>
  );
};
